/**
 * AdminPaymentHealthWidget — monitoring temps réel des paiements.
 *
 * Affiche :
 *   • KPI rapides (pending, stuck, revenu du jour)
 *   • Statut santé global (vert / orange / rouge)
 *   • Liste des réservations bloquées > 30 min avec action « Réconcilier »
 *   • Refresh auto toutes les 30 s + bouton manuel
 *
 * Endpoints :
 *   GET  /api/admin/bookings/health
 *   GET  /api/admin/bookings/stuck?older_than_minutes=30
 *   POST /api/admin/bookings/reconcile-pending
 *   POST /api/admin/bookings/:id/reconcile
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Activity, AlertTriangle, CheckCircle2, Clock, Euro, Loader2,
  RefreshCw, TrendingUp, Wrench, ShieldAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl } from '../../config/environment';
import { formatDateFR, formatTimeFR } from '../../utils/formatFR';

const API = getApiUrl();
const REFRESH_MS = 30000;

function severityOf(health) {
  if (!health) return 'unknown';
  if (health.stuck_30m > 0) return 'critical';
  if (health.stuck_10m > 0) return 'warning';
  return 'healthy';
}

const SEVERITY_STYLES = {
  healthy:  { dot: 'bg-emerald-500', label: 'Opérationnel',         chip: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
  warning:  { dot: 'bg-amber-500',   label: 'Anomalie mineure',     chip: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
  critical: { dot: 'bg-red-500',     label: 'Intervention requise', chip: 'bg-red-500/10 text-red-400 border-red-500/20' },
  unknown:  { dot: 'bg-[#92710A]',    label: 'Chargement...',        chip: 'bg-[#92710A]/10 text-[#4B5563] border-zinc-500/20' },
};

function Kpi({ icon: Icon, label, value, sub, color, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-[#E8E2D2] rounded-xl p-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${color}20` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <div className="min-w-0">
          <p className="text-xl font-light text-[#2A2319] tabular-nums">{value}</p>
          <p className="text-xs text-[#6B7280] truncate">{label}</p>
          {sub && <p className="text-[11px] text-[#9CA3AF] truncate">{sub}</p>}
        </div>
      </div>
    </div>
  );
}

export default function AdminPaymentHealthWidget() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [health, setHealth] = useState(null);
  const [stuck, setStuck] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reconciling, setReconciling] = useState(false);
  const [fixingId, setFixingId] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [hRes, sRes] = await Promise.all([
        fetch(`${API}/admin/bookings/health`, { headers }),
        fetch(`${API}/admin/bookings/stuck?older_than_minutes=30`, { headers }),
      ]);
      if (hRes.ok) setHealth(await hRes.json());
      if (sRes.ok) {
        const data = await sRes.json();
        setStuck(data.bookings || []);
      }
    } catch (e) {
      console.error('[PaymentHealth] fetch failed', e);
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    fetchAll();
    const iv = setInterval(fetchAll, REFRESH_MS);
    return () => clearInterval(iv);
  }, [fetchAll]);

  const handleReconcileAll = async () => {
    setReconciling(true);
    try {
      const res = await fetch(`${API}/admin/bookings/reconcile-pending`, {
        method: 'POST', headers,
      });
      if (!res.ok) throw new Error('Échec réconciliation');
      const data = await res.json();
      toast.success(
        `Réconciliation : ${data.checked} vérifié(s), ${data.confirmed} confirmé(s), ${data.expired} expiré(s)`
      );
      await fetchAll();
    } catch (e) {
      toast.error(e.message);
    } finally { setReconciling(false); }
  };

  const handleReconcileOne = async (id) => {
    setFixingId(id);
    try {
      const res = await fetch(`${API}/admin/bookings/${id}/reconcile`, {
        method: 'POST', headers,
      });
      if (!res.ok) throw new Error('Échec');
      const data = await res.json();
      toast.success(`Booking mis à jour : ${data.status} / ${data.payment_status}`);
      await fetchAll();
    } catch (e) {
      toast.error(e.message);
    } finally { setFixingId(null); }
  };

  const sev = severityOf(health);
  const s = SEVERITY_STYLES[sev];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="payment-health-loading">
        <Loader2 className="w-5 h-5 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="payment-health-widget">
      {/* Header statut */}
      <div className="bg-white border border-[#E8E2D2] rounded-xl p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="relative">
              <span className={`block w-2.5 h-2.5 rounded-full ${s.dot}`} />
              <span className={`absolute inset-0 w-2.5 h-2.5 rounded-full ${s.dot} opacity-60 animate-ping`} />
            </div>
            <div>
              <p className="uppercase text-[10px] tracking-[0.2em] text-[#6B7280]">Santé paiements</p>
              <p className="text-[#2A2319] text-sm font-medium" data-testid="payment-health-status">
                {s.label}
              </p>
            </div>
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${s.chip}`}>
              {sev === 'healthy' && <CheckCircle2 className="w-3 h-3" />}
              {sev === 'warning' && <AlertTriangle className="w-3 h-3" />}
              {sev === 'critical' && <ShieldAlert className="w-3 h-3" />}
              {sev.toUpperCase()}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={fetchAll}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FAF7EE] hover:bg-[#F1ECDC] text-[#374151] text-xs transition"
              data-testid="payment-health-refresh"
              title="Actualiser"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Actualiser
            </button>
            <button
              onClick={handleReconcileAll}
              disabled={reconciling || (health?.pending_total ?? 0) === 0}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#D4AF37] hover:bg-[#c49b2a] disabled:opacity-40 disabled:cursor-not-allowed text-[#0A0A0B] text-xs font-semibold transition"
              data-testid="payment-health-reconcile-all"
              title="Force la réconciliation Stripe de tous les pending"
            >
              {reconciling ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Réconciliation…</>
              ) : (
                <><Wrench className="w-3.5 h-3.5" /> Réconcilier maintenant</>
              )}
            </button>
          </div>
        </div>

        {health?.last_reconciliation_at && (
          <p className="text-[11px] text-[#9CA3AF] mt-3 pl-5">
            Cron auto — dernière exécution {new Date(health.last_reconciliation_at).toLocaleString('fr-FR')}
          </p>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi
          icon={Clock}
          label="En attente paiement"
          value={health?.pending_total ?? 0}
          sub={`${(health?.pending_amount_eur ?? 0).toFixed(0)} € en jeu`}
          color="#F59E0B"
          testid="payment-kpi-pending"
        />
        <Kpi
          icon={AlertTriangle}
          label="Bloqués > 10 min"
          value={health?.stuck_10m ?? 0}
          sub="webhook en retard"
          color="#F97316"
          testid="payment-kpi-stuck10"
        />
        <Kpi
          icon={ShieldAlert}
          label="Anomalies > 30 min"
          value={health?.stuck_30m ?? 0}
          sub={`${(health?.stuck_amount_eur ?? 0).toFixed(0)} € bloqués`}
          color="#EF4444"
          testid="payment-kpi-stuck30"
        />
        <Kpi
          icon={TrendingUp}
          label="CA du jour"
          value={`${(health?.revenue_today_eur ?? 0).toFixed(0)} €`}
          sub={`${health?.confirmed_today ?? 0} session${(health?.confirmed_today ?? 0) > 1 ? 's' : ''} confirmée${(health?.confirmed_today ?? 0) > 1 ? 's' : ''}`}
          color="#10B981"
          testid="payment-kpi-revenue"
        />
      </div>

      {/* Secondary row */}
      <div className="grid grid-cols-2 gap-3">
        <Kpi
          icon={Activity}
          label="En cours checkout"
          value={health?.pending_recent ?? 0}
          sub="< 10 min (normal)"
          color="#60A5FA"
          testid="payment-kpi-recent"
        />
        <Kpi
          icon={Euro}
          label="Annulés aujourd'hui"
          value={health?.cancelled_today ?? 0}
          sub="réservations abandonnées"
          color="#A78BFA"
          testid="payment-kpi-cancelled"
        />
      </div>

      {/* Anomalies list */}
      <div className="bg-white border border-[#E8E2D2] rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-[#F1ECDC] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-red-400" />
            <h3 className="text-[#2A2319] text-sm font-medium">
              Anomalies &gt; 30 min
            </h3>
            <span className="text-xs text-[#6B7280]">({stuck.length})</span>
          </div>
        </div>

        {stuck.length === 0 ? (
          <div className="px-4 py-8 text-center text-[#6B7280] text-sm" data-testid="payment-anomalies-empty">
            <CheckCircle2 className="w-6 h-6 mx-auto mb-2 text-emerald-500" />
            Aucune anomalie détectée — tous les paiements sont à jour.
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {stuck.map(b => (
              <div
                key={b.booking_id}
                data-testid={`payment-anomaly-${b.booking_id}`}
                className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 hover:bg-[#FAF7EE] transition"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[#2A2319] text-sm font-medium truncate">
                      {b.client_name || b.client_email || 'Client inconnu'}
                    </span>
                    <span className="text-xs text-[#6B7280]">→</span>
                    <span className="text-xs text-[#4B5563] truncate">{b.coach_name || '—'}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-[11px] text-[#6B7280]">
                    <span>{formatDateFR(b.date)} {formatTimeFR(b.start_time)}</span>
                    <span className="text-amber-400">{b.age_minutes} min d'ancienneté</span>
                    {b.amount != null && (
                      <span className="text-[#4B5563]">{Number(b.amount).toFixed(0)} €</span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleReconcileOne(b.booking_id)}
                  disabled={fixingId === b.booking_id}
                  data-testid={`payment-anomaly-fix-${b.booking_id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 text-[#D4AF37] text-xs font-medium transition disabled:opacity-40"
                >
                  {fixingId === b.booking_id ? (
                    <><Loader2 className="w-3.5 h-3.5 animate-spin" /> …</>
                  ) : (
                    <><Wrench className="w-3.5 h-3.5" /> Réconcilier</>
                  )}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
